import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Tooltip, Divider, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Autocomplete, Menu, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon, Print as PrintIcon,
  Share as ShareIcon,
  Send as SendIcon, MoreVert as MoreIcon, AutoAwesome as AiIcon,
  Person as PersonIcon, LocalHospital as HospitalIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';

import SendDocumentDialog from '../../components/SendDocumentDialog';

const statusLabels: Record<string, string> = {
  pending: 'Pendente', sent: 'Enviado', in_treatment: 'Em Tratamento',
  completed: 'Concluído', returned: 'Retornou', cancelled: 'Cancelado',
};
const statusColors: Record<string, string> = {
  pending: '#f59e0b', sent: '#3b82f6', in_treatment: '#8b5cf6',
  completed: '#22c55e', returned: '#14B8A6', cancelled: '#ef4444',
};
const urgencyLabels: Record<string, string> = {
  routine: 'Rotina', priority: 'Prioritário', urgent: 'Urgente', emergency: 'Emergência',
};

function escapeHtmlAttr(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

const Referrals: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();

  const [referrals, setReferrals] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuItem, setMenuItem] = useState<any>(null);

  // AI referral states
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPatientName, setAiPatientName] = useState('');
  const [aiReason, setAiReason] = useState('');
  const [aiSpecialty, setAiSpecialty] = useState('');
  const [aiError, setAiError] = useState('');
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendDocData, setSendDocData] = useState<any>(null);

  const [form, setForm] = useState<any>({
    patient_id: '', referring_professional_id: '', issue_date: new Date().toISOString().split('T')[0],
    destination_specialty: '', destination_professional: '', destination_clinic: '',
    reason: '', clinical_summary: '', urgency: 'routine', notes: '',
  });
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [profOptions, setProfOptions] = useState<any[]>([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [profSearch, setProfSearch] = useState('');
  const lastPatientSearchRef = useRef<string>('');
  const lastProfSearchRef = useRef<string>('');

  useEffect(() => { loadData(); }, []);
  useEffect(() => { loadReferrals(); }, [search, filterStatus]);

  // Carregar pacientes e profissionais quando o dialog abrir
  useEffect(() => {
    if (openDialog) {
      searchPatients('');
      searchProfessionals('');
    }
  }, [openDialog]);

  // Buscar pacientes com debounce (evitar loop infinito)
  useEffect(() => {
    if (patientSearch === lastPatientSearchRef.current) {
      return;
    }
    const timer = setTimeout(() => {
      if (openDialog && patientSearch !== undefined) {
        lastPatientSearchRef.current = patientSearch;
        searchPatients(patientSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [patientSearch, openDialog]);

  // Buscar profissionais com debounce (evitar loop infinito)
  useEffect(() => {
    if (profSearch === lastProfSearchRef.current) {
      return;
    }
    const timer = setTimeout(() => {
      if (openDialog && profSearch !== undefined) {
        lastProfSearchRef.current = profSearch;
        searchProfessionals(profSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [profSearch, openDialog]);

  const loadData = async () => { await Promise.all([loadReferrals(), loadStats()]); };

  const loadReferrals = async () => {
    try {
      setLoading(true);
      const params: any = { limit: 50 };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      const { data } = await axios.get(getApiUrl('/clinic/referrals'), { params });
      setReferrals(data.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const loadStats = async () => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/referrals/stats'));
      setStats(data.data || {});
    } catch (e) { console.error(e); }
  };

  const searchPatients = async (q: string) => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/budgets-search/patients'), { params: { search: q || '' } });
      if (data?.success !== false) {
        setPatientOptions(data?.data || []);
      } else {
        setPatientOptions([]);
      }
    } catch (e) { 
      console.error('Error searching patients:', e);
      setPatientOptions([]);
    }
  };

  const searchProfessionals = async (q: string) => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/budgets-search/professionals'), { params: { search: q || '' } });
      if (data?.success !== false) {
        setProfOptions(data?.data || []);
      } else {
        setProfOptions([]);
      }
    } catch (e) { 
      console.error('Error searching professionals:', e);
      setProfOptions([]);
    }
  };

  // Função para limpar e mapear dados do formulário para o formato esperado pelo backend
  const cleanFormData = (data: any) => {
    // Campos obrigatórios que não podem ser null
    const requiredFields = ['reason', 'destination_specialty'];
    
    const cleaned: any = {
      patient_id: data.patient_id || null,
      referring_professional_id: data.referring_professional_id || null,
      issue_date: data.issue_date || new Date().toISOString().split('T')[0],
      destination_specialty: data.destination_specialty || '',
      destination_professional: data.destination_professional || null,
      destination_clinic: data.destination_clinic || null,
      reason: data.reason || '',
      clinical_summary: data.clinical_summary || null,
      urgency: data.urgency || 'routine',
      notes: data.notes || null,
    };

    // Remover campos vazios, mas preservar campos obrigatórios
    Object.keys(cleaned).forEach(key => {
      // Não converter campos obrigatórios para null
      if (requiredFields.includes(key)) {
        // Garantir que campos obrigatórios tenham pelo menos string vazia
        if (cleaned[key] === null || cleaned[key] === undefined) {
          cleaned[key] = '';
        }
      } else {
        // Campos opcionais podem ser null
        if (cleaned[key] === '' || cleaned[key] === undefined) {
          cleaned[key] = null;
        }
      }
    });

    return cleaned;
  };

  const handleSave = async () => {
    // Validar campos obrigatórios
    if (!form.reason || !form.destination_specialty) {
      setSnackbar({ open: true, message: 'Motivo do encaminhamento e especialidade destino são obrigatórios', severity: 'error' });
      return;
    }

    try {
      const cleanedData = cleanFormData(form);
      
      if (editing) {
        await axios.put(getApiUrl(`/clinic/referrals/${editing.id}`), cleanedData);
        setSnackbar({ open: true, message: 'Encaminhamento atualizado!', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/clinic/referrals'), cleanedData);
        setSnackbar({ open: true, message: 'Encaminhamento criado!', severity: 'success' });
      }
      setOpenDialog(false); resetForm(); loadData();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Erro ao salvar', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Deseja excluir?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/referrals/${id}`));
      setSnackbar({ open: true, message: 'Excluído!', severity: 'success' }); loadData();
    } catch (e) { setSnackbar({ open: true, message: 'Erro ao excluir', severity: 'error' }); }
  };

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await axios.patch(getApiUrl(`/clinic/referrals/${id}/status`), { status });
      setSnackbar({ open: true, message: 'Status atualizado!', severity: 'success' }); loadData();
    } catch (e) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  const handlePrint = async (id: string) => {
    try {
      const { data } = await axios.get(getApiUrl(`/clinic/referrals/${id}/print`));
      const ref = data.data.referral;
      const clinic = data.data.clinic;
      const patient = ref?.patient_snapshot || {};
      const prof = ref?.professional_snapshot || {};
      const profSigUrl = String(prof?.signature_image_url ?? '').trim();
      const profSigBlock = profSigUrl
        ? `<div class="prof-sig-print"><img src="${escapeHtmlAttr(profSigUrl)}" alt="" /></div>`
        : '';
      const w = window.open('', '_blank');
      if (!w) return;
      w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Encaminhamento</title>
<style>body{font-family:Arial,sans-serif;max-width:700px;margin:0 auto;padding:40px;color:#333}
.header{text-align:center;border-bottom:2px solid #14B8A6;padding-bottom:20px;margin-bottom:20px}
.clinic-name{font-size:22px;font-weight:bold;color:#14B8A6} .clinic-info{font-size:12px;color:#666;margin-top:5px}
.title{text-align:center;font-size:18px;font-weight:bold;margin:20px 0;text-transform:uppercase}
.field{margin:8px 0;font-size:14px} .field strong{color:#14B8A6}
.content{font-size:14px;line-height:1.8;text-align:justify;margin:20px 0;padding:15px;border:1px solid #e2e8f0;border-radius:8px}
.signature{margin-top:60px;text-align:center} .signature-line{border-top:1px solid #333;width:300px;margin:0 auto;padding-top:5px}
.prof-sig-print{margin-bottom:8px} .prof-sig-print img{max-height:90px;max-width:360px;object-fit:contain;vertical-align:bottom}
</style></head><body>
<div class="header"><div class="clinic-name">${clinic?.clinic_name || 'Clínica'}</div>
<div class="clinic-info">${clinic?.clinic_address || ''}<br/>${clinic?.clinic_phone || ''}</div></div>
<div class="title">Guia de Encaminhamento ${ref?.referral_number ? '- ' + ref.referral_number : ''}</div>
<div class="field"><strong>Paciente:</strong> ${patient?.full_name || 'N/A'}</div>
${patient?.cpf ? `<div class="field"><strong>CPF:</strong> ${patient.cpf}</div>` : ''}
<div class="field"><strong>Data:</strong> ${ref?.issue_date ? new Date(ref.issue_date + 'T12:00:00').toLocaleDateString('pt-BR') : ''}</div>
<div class="field"><strong>Especialidade Destino:</strong> ${ref?.destination_specialty || '-'}</div>
${ref?.destination_professional ? `<div class="field"><strong>Profissional Destino:</strong> ${ref.destination_professional}</div>` : ''}
${ref?.destination_clinic ? `<div class="field"><strong>Clínica Destino:</strong> ${ref.destination_clinic}</div>` : ''}
<div class="field"><strong>Urgência:</strong> ${urgencyLabels[ref?.urgency] || ref?.urgency || '-'}</div>
<div class="field"><strong>Motivo do Encaminhamento:</strong></div>
<div class="content">${(ref?.reason || '').replace(/\n/g, '<br/>')}</div>
${ref?.clinical_summary ? `<div class="field"><strong>Resumo Clínico:</strong></div><div class="content">${ref.clinical_summary.replace(/\n/g, '<br/>')}</div>` : ''}
<div class="signature">${profSigBlock}<div class="signature-line">${prof?.full_name || ''}<br/>
${prof?.registration_type || 'CRM'} ${prof?.registration_number || ''} ${prof?.registration_state ? '/' + prof.registration_state : ''}</div></div>
</body></html>`);
      w.document.close();
      setTimeout(() => w.print(), 500);
    } catch (e) { setSnackbar({ open: true, message: 'Erro ao imprimir', severity: 'error' }); }
  };

  const handleEdit = (ref: any) => {
    setEditing(ref);
    setForm({
      patient_id: ref.patient_id || '', referring_professional_id: ref.referring_professional_id || '',
      issue_date: ref.issue_date || new Date().toISOString().split('T')[0],
      destination_specialty: ref.destination_specialty || '', destination_professional: ref.destination_professional || '',
      destination_clinic: ref.destination_clinic || '', reason: ref.reason || '',
      clinical_summary: ref.clinical_summary || '', urgency: ref.urgency || 'routine', notes: ref.notes || '',
    });
    if (ref.patient_snapshot) setPatientOptions([{ id: ref.patient_id, full_name: ref.patient_snapshot?.full_name || ref.patient_name }]);
    if (ref.professional_snapshot) setProfOptions([{ id: ref.referring_professional_id, full_name: ref.professional_snapshot?.full_name || ref.professional_name }]);
    // Limpar refs para evitar loop
    lastPatientSearchRef.current = '';
    lastProfSearchRef.current = '';
    setPatientSearch('');
    setProfSearch('');
    setOpenDialog(true);
  };

  const resetForm = () => {
    setForm({
      patient_id: '', referring_professional_id: '', issue_date: new Date().toISOString().split('T')[0],
      destination_specialty: '', destination_professional: '', destination_clinic: '',
      reason: '', clinical_summary: '', urgency: 'routine', notes: '',
    });
    setPatientOptions([]); 
    setProfOptions([]);
    setPatientSearch('');
    setProfSearch('');
    lastPatientSearchRef.current = '';
    lastProfSearchRef.current = '';
  };

  const handleAiGenerate = async () => {
    if (!aiPatientName.trim() || !aiReason.trim()) {
      setAiError('Preencha o nome do paciente e o motivo do encaminhamento');
      return;
    }
    setAiGenerating(true);
    setAiError('');
    try {
      const res = await axios.post(getApiUrl('/clinic/referrals/ai-generate'), {
        patient_name: aiPatientName.trim(),
        reason: aiReason.trim(),
        destination_specialty: aiSpecialty.trim(),
      });

      const aiData = res.data?.data;
      if (!aiData) throw new Error('Resposta vazia da IA');

      const rawText = typeof aiData === 'string' ? aiData : (aiData.output || aiData.text || aiData.response || aiData.referral || JSON.stringify(aiData));
      const parsed = parseAiReferral(rawText, aiReason.trim(), aiSpecialty.trim());

      resetForm();
      setForm((f: any) => ({
        ...f,
        destination_specialty: parsed.destination_specialty || aiSpecialty.trim() || '',
        destination_professional: parsed.destination_professional || '',
        destination_clinic: parsed.destination_clinic || '',
        reason: parsed.reason || aiReason.trim(),
        clinical_summary: parsed.clinical_summary || '',
        urgency: parsed.urgency || 'routine',
        notes: parsed.notes || '',
      }));

      setAiDialogOpen(false);
      setEditing(null);
      setOpenDialog(true);
      setSnackbar({ open: true, message: 'Encaminhamento gerado com IA! Revise e ajuste antes de salvar.', severity: 'success' });
    } catch (err: any) {
      setAiError(err.response?.data?.error || err.message || 'Erro ao gerar encaminhamento com IA');
    } finally {
      setAiGenerating(false);
    }
  };

  const parseAiReferral = (text: string, fallbackReason: string, fallbackSpecialty: string) => {
    const result: any = {
      destination_specialty: '', destination_professional: '', destination_clinic: '',
      reason: '', clinical_summary: '', urgency: 'routine', notes: '',
    };
    const lines = text.split('\n').map((l: string) => l.trim()).filter((l: string) => l);
    const reasonLines: string[] = [];
    const summaryLines: string[] = [];
    let currentBlock: 'reason' | 'summary' | null = null;

    for (const line of lines) {
      const lower = line.toLowerCase();

      if (lower.startsWith('especialidade destino:') || lower.startsWith('especialidade:') || lower.startsWith('encaminhar para:')) {
        result.destination_specialty = line.split(':').slice(1).join(':').trim();
        currentBlock = null;
        continue;
      }
      if (lower.startsWith('profissional destino:') || lower.startsWith('médico destino:') || lower.startsWith('medico destino:') || lower.startsWith('dr.') || lower.startsWith('dra.')) {
        if (lower.startsWith('dr.') || lower.startsWith('dra.')) {
          result.destination_professional = line.trim();
        } else {
          result.destination_professional = line.split(':').slice(1).join(':').trim();
        }
        currentBlock = null;
        continue;
      }
      if (lower.startsWith('clínica destino:') || lower.startsWith('clinica destino:') || lower.startsWith('hospital destino:') || lower.startsWith('instituição:') || lower.startsWith('instituicao:')) {
        result.destination_clinic = line.split(':').slice(1).join(':').trim();
        currentBlock = null;
        continue;
      }
      if (lower.startsWith('urgência:') || lower.startsWith('urgencia:') || lower.startsWith('prioridade:')) {
        const val = line.split(':').slice(1).join(':').trim().toLowerCase();
        if (val.includes('emergência') || val.includes('emergencia') || val.includes('emergency')) result.urgency = 'emergency';
        else if (val.includes('urgente') || val.includes('urgent')) result.urgency = 'urgent';
        else if (val.includes('priorit')) result.urgency = 'priority';
        else result.urgency = 'routine';
        currentBlock = null;
        continue;
      }
      if (lower.startsWith('motivo:') || lower.startsWith('motivo do encaminhamento:') || lower.startsWith('razão:') || lower.startsWith('razao:') || lower.startsWith('indicação:') || lower.startsWith('indicacao:')) {
        currentBlock = 'reason';
        const afterColon = line.split(':').slice(1).join(':').trim();
        if (afterColon) reasonLines.push(afterColon);
        continue;
      }
      if (lower.startsWith('resumo clínico:') || lower.startsWith('resumo clinico:') || lower.startsWith('histórico:') || lower.startsWith('historico:') || lower.startsWith('quadro clínico:') || lower.startsWith('quadro clinico:') || lower.startsWith('história clínica:') || lower.startsWith('historia clinica:')) {
        currentBlock = 'summary';
        const afterColon = line.split(':').slice(1).join(':').trim();
        if (afterColon) summaryLines.push(afterColon);
        continue;
      }
      if (lower.startsWith('observaç') || lower.startsWith('observac') || lower.startsWith('notas:') || lower.startsWith('nota:') || lower.startsWith('informações adicionais:') || lower.startsWith('informacoes adicionais:')) {
        result.notes = line.split(':').slice(1).join(':').trim();
        currentBlock = null;
        continue;
      }
      if (lower.startsWith('hipótese diagnóstica:') || lower.startsWith('hipotese diagnostica:') || lower.startsWith('diagnóstico:') || lower.startsWith('diagnostico:') || lower.startsWith('cid:')) {
        summaryLines.push(line);
        currentBlock = 'summary';
        continue;
      }

      if (currentBlock === 'reason') {
        reasonLines.push(line);
      } else if (currentBlock === 'summary') {
        summaryLines.push(line);
      } else if (line.length > 30 && !line.includes(':')) {
        if (reasonLines.length === 0) {
          currentBlock = 'reason';
          reasonLines.push(line);
        } else {
          currentBlock = 'summary';
          summaryLines.push(line);
        }
      }
    }

    if (reasonLines.length > 0) result.reason = reasonLines.join('\n');
    if (summaryLines.length > 0) result.clinical_summary = summaryLines.join('\n');
    if (!result.reason) result.reason = fallbackReason;
    if (!result.destination_specialty) result.destination_specialty = fallbackSpecialty;

    return result;
  };

  return (
    <Box sx={clinicPageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>Controle de Encaminhamentos</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Gerencie guias de encaminhamento para especialistas</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={loadData} sx={{ color: '#14B8A6' }}><RefreshIcon /></IconButton>
          <Button variant="outlined" startIcon={<AiIcon />}
            onClick={() => { setAiPatientName(''); setAiReason(''); setAiSpecialty(''); setAiError(''); setAiDialogOpen(true); }}
            sx={{ borderColor: '#8b5cf6', color: '#8b5cf6', '&:hover': { borderColor: '#7c3aed', bgcolor: 'rgba(139,92,246,0.04)' }, textTransform: 'none', fontWeight: 600 }}>
            Criar com IA
          </Button>
          <Button variant="contained" startIcon={<AddIcon />}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }} onClick={() => { setEditing(null); resetForm(); setOpenDialog(true); }}>
            Novo Encaminhamento
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: stats.total || 0, color: '#14B8A6' },
          { label: 'Pendentes', value: stats.total_pending || 0, color: '#f59e0b' },
          { label: 'Este Mês', value: stats.total_month || 0, color: '#3b82f6' },
          { label: 'Concluídos', value: stats.total_completed || 0, color: '#22c55e' },
          { label: 'Retornaram', value: stats.total_returned || 0, color: '#8b5cf6' },
        ].map((kpi, i) => (
          <Grid item xs={6} sm={4} md={2.4} key={i}>
            <Card sx={{ bgcolor: 'white', borderRadius: 3, border: `1px solid ${kpi.color}22` }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" sx={{ color: '#64748b' }}>{kpi.label}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: kpi.color }}>{kpi.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', p: 2, '&:last-child': { pb: 2 } }}>
          <TextField size="small" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} sx={{ minWidth: 250 }} />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(statusLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 600 }}>Nº</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Paciente</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Especialidade Destino</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Urgência</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={32} /></TableCell></TableRow>
              ) : referrals.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#94a3b8' }}>Nenhum encaminhamento</TableCell></TableRow>
              ) : referrals.map((ref) => (
                <TableRow key={ref.id} hover>
                  <TableCell sx={{ fontWeight: 600, color: '#14B8A6' }}>{ref.referral_number || '-'}</TableCell>
                  <TableCell>{ref.patient_name || '-'}</TableCell>
                  <TableCell>{ref.destination_specialty || '-'}</TableCell>
                  <TableCell><Chip label={urgencyLabels[ref.urgency] || ref.urgency} size="small"
                    sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: ref.urgency === 'urgent' || ref.urgency === 'emergency' ? '#fef2f2' : '#f0fdf4', color: ref.urgency === 'urgent' || ref.urgency === 'emergency' ? '#ef4444' : '#22c55e' }} /></TableCell>
                  <TableCell><Chip label={statusLabels[ref.status] || ref.status} size="small"
                    sx={{ bgcolor: (statusColors[ref.status] || '#666') + '15', color: statusColors[ref.status] || '#666', fontWeight: 600, fontSize: '0.7rem' }} /></TableCell>
                  <TableCell>{ref.issue_date ? new Date(ref.issue_date + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handlePrint(ref.id)} sx={{ color: '#14B8A6' }}><PrintIcon fontSize="small" /></IconButton>
                    <Tooltip title="Enviar"><IconButton size="small" onClick={() => { const p = ref.patient_snapshot || {}; setSendDocData({ id: ref.id, patientId: ref.patient_id, email: p.email, phone: p.phone, whatsapp: p.whatsapp, name: p.full_name || ref.patient_name }); setSendDialogOpen(true); }} sx={{ color: '#3b82f6' }}><ShareIcon fontSize="small" /></IconButton></Tooltip>
                    <IconButton size="small" onClick={() => handleEdit(ref)} sx={{ color: '#3b82f6' }}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuItem(ref); }}><MoreIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {menuItem && menuItem.status === 'pending' && <MenuItem onClick={() => { handleStatusChange(menuItem?.id, 'sent'); setMenuAnchor(null); }}>
          <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon><ListItemText>Marcar como Enviado</ListItemText></MenuItem>}
        {menuItem && menuItem.status !== 'completed' && <MenuItem onClick={() => { handleStatusChange(menuItem?.id, 'completed'); setMenuAnchor(null); }}>
          <ListItemIcon><SendIcon fontSize="small" sx={{ color: '#22c55e' }} /></ListItemIcon><ListItemText>Marcar como Concluído</ListItemText></MenuItem>}
        <MenuItem onClick={() => { handleDelete(menuItem?.id); setMenuAnchor(null); }} sx={{ color: '#ef4444' }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon><ListItemText>Excluir</ListItemText></MenuItem>
      </Menu>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{editing ? 'Editar' : 'Novo'} Encaminhamento</Typography>
          <IconButton onClick={() => setOpenDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete 
                size="small" 
                options={patientOptions} 
                getOptionLabel={(o: any) => `${o.full_name || ''}${o.cpf ? ` - CPF: ${o.cpf}` : ''}${o.phone ? ` - Tel: ${o.phone}` : ''}`}
                onInputChange={(_, v, reason) => {
                  if (reason !== 'reset') {
                    setPatientSearch(v);
                  }
                }}
                onChange={(_, v) => {
                  setForm({ ...form, patient_id: v?.id || '' });
                  if (v) {
                    lastPatientSearchRef.current = '';
                    setPatientSearch('');
                  }
                }}
                onOpen={() => {
                  if (patientOptions.length === 0) {
                    lastPatientSearchRef.current = '';
                    setPatientSearch('');
                  }
                }}
                value={patientOptions.find((p: any) => p.id === form.patient_id) || null}
                renderInput={(params) => <TextField {...params} label="Paciente" placeholder="Buscar por nome, CPF ou telefone..." />}
                isOptionEqualToValue={(o: any, v: any) => o?.id === v?.id}
                filterOptions={(x) => x}
                loading={false}
                noOptionsText="Nenhum paciente encontrado"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete 
                size="small" 
                options={profOptions} 
                getOptionLabel={(o: any) => `${o.full_name || ''} - ${o.registration_type || 'CRM'} ${o.registration_number || ''} ${o.registration_state || ''} ${o.specialty ? `(${o.specialty})` : ''}`}
                onInputChange={(_, v, reason) => {
                  if (reason !== 'reset') {
                    setProfSearch(v);
                  }
                }}
                onChange={(_, v) => {
                  setForm({ ...form, referring_professional_id: v?.id || '' });
                  if (v) {
                    lastProfSearchRef.current = '';
                    setProfSearch('');
                  }
                }}
                onOpen={() => {
                  if (profOptions.length === 0) {
                    lastProfSearchRef.current = '';
                    setProfSearch('');
                  }
                }}
                value={profOptions.find((p: any) => p.id === form.referring_professional_id) || null}
                renderInput={(params) => <TextField {...params} label="Profissional Solicitante" placeholder="Buscar por nome ou registro..." />}
                isOptionEqualToValue={(o: any, v: any) => o?.id === v?.id}
                filterOptions={(x) => x}
                loading={false}
                noOptionsText="Nenhum profissional encontrado"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" type="date" label="Data" value={form.issue_date}
                onChange={(e) => setForm({ ...form, issue_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Urgência</InputLabel>
                <Select value={form.urgency} label="Urgência" onChange={(e) => setForm({ ...form, urgency: e.target.value })}>
                  {Object.entries(urgencyLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Especialidade Destino" value={form.destination_specialty}
                onChange={(e) => setForm({ ...form, destination_specialty: e.target.value })} placeholder="Ex: Cardiologia" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Profissional Destino" value={form.destination_professional}
                onChange={(e) => setForm({ ...form, destination_professional: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Clínica/Hospital Destino" value={form.destination_clinic}
                onChange={(e) => setForm({ ...form, destination_clinic: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={4} label="Motivo do Encaminhamento" value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Resumo Clínico" value={form.clinical_summary}
                onChange={(e) => setForm({ ...form, clinical_summary: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Observações" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' } }}>
            {editing ? 'Atualizar' : 'Criar Encaminhamento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Referral Dialog */}
      <Dialog open={aiDialogOpen} onClose={() => !aiGenerating && setAiDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AiIcon sx={{ color: '#8b5cf6' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Encaminhamento com IA</Typography>
          </Box>
          <IconButton onClick={() => !aiGenerating && setAiDialogOpen(false)} disabled={aiGenerating}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3, mt: 1 }}>
            <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
              A IA irá gerar uma guia de encaminhamento completa com motivo detalhado, resumo clínico, especialidade destino e nível de urgência. Revise sempre antes de salvar.
            </Alert>
            {aiError && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{aiError}</Alert>}
          </Box>
          <TextField
            fullWidth
            label="Nome do Paciente *"
            placeholder="Ex: Maria Silva"
            value={aiPatientName}
            onChange={(e) => setAiPatientName(e.target.value)}
            disabled={aiGenerating}
            sx={{ mb: 2.5 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }}
          />
          <TextField
            fullWidth
            label="Especialidade Destino (opcional)"
            placeholder="Ex: Cardiologia, Ortopedia... (se vazio, a IA sugere)"
            value={aiSpecialty}
            onChange={(e) => setAiSpecialty(e.target.value)}
            disabled={aiGenerating}
            sx={{ mb: 2.5 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><HospitalIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }}
          />
          <TextField
            fullWidth
            label="Motivo / Quadro Clínico *"
            placeholder="Ex: Paciente com dores no peito recorrentes, ECG com alterações, necessita avaliação cardiológica especializada"
            value={aiReason}
            onChange={(e) => setAiReason(e.target.value)}
            disabled={aiGenerating}
            multiline
            rows={4}
            onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey && !aiGenerating) { e.preventDefault(); handleAiGenerate(); } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SendIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb', gap: 1 }}>
          <Button onClick={() => setAiDialogOpen(false)} disabled={aiGenerating} sx={{ color: '#6b7280', textTransform: 'none' }}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAiGenerate}
            disabled={aiGenerating || !aiPatientName.trim() || !aiReason.trim()}
            startIcon={aiGenerating ? <CircularProgress size={18} color="inherit" /> : <AiIcon />}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600, minWidth: 180 }}
          >
            {aiGenerating ? 'Gerando encaminhamento...' : 'Gerar com IA'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>

      <SendDocumentDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        documentType="referral"
        documentId={sendDocData?.id || ''}
        patientId={sendDocData?.patientId}
        patientEmail={sendDocData?.email}
        patientPhone={sendDocData?.phone}
        patientWhatsapp={sendDocData?.whatsapp}
        patientName={sendDocData?.name}
        documentLabel="Encaminhamento"
      />
    </Box>
  );
};

export default Referrals;
